<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { forecastApi } from '../lib/api';
  import type { ForecastData, ForecastParams, TimeSeriesData } from '../lib/types';
  import { formatDate } from '../lib/utils';
  import { t } from '../lib/i18n';
  import { compareIds } from '../lib/id-utils';
  import StationarityGuide from './StationarityGuide.svelte';
  
  export let timeseriesId: string;
  export let timeseriesData: TimeSeriesData | null = null;
  export let forecasts: ForecastData[] = [];
  export let isLoading: boolean = false;
  
  // Статус стационарности ряда (будет получаться из анализа)
  let isStationaryAdf: boolean | null = null;
  let isStationaryKpss: boolean | null = null;
  let showStationarityGuide = false;
  
  // Check if data is available (useful for future UI conditionals)
  $: dataAvailable = timeseriesData && timeseriesData.data_points && timeseriesData.data_points.length > 0;
  // Use the variable in a dummy block to prevent unused variable warning
  $: { void(dataAvailable); }
  
  const dispatch = createEventDispatcher();
  
  let showCreateForm = false;
  let formError = '';
  let selectedForecast: ForecastData | null = null;
  let selectedAlgorithm = 'arima';
  let forecastHorizon = 7;
  let confidenceLevel = 0.95;
  const arima = { p: 1, d: 1, q: 0 };
  const sarima = { p: 1, d: 1, q: 0, P: 1, D: 0, Q: 0, m: 7 };
  const ets = { trend: 'add', seasonal: null, seasonal_periods: 7 };
  let formSubmitting = false;
  
  // Toggle forecast points in the main chart
  function selectForecast(forecast: ForecastData) {
    if (selectedForecast && compareIds(selectedForecast.id, forecast.id)) {
      selectedForecast = null;
      dispatch('forecastSelected', { forecastId: null });
    } else {
      selectedForecast = forecast;
      loadForecastDetails(forecast.id);
    }
  }
  
  // Load forecast details including points
  async function loadForecastDetails(forecastId: string | number) {
    try {
      isLoading = true;
      formError = '';
      
      const forecastWithPoints = await forecastApi.getById(timeseriesId, forecastId, true);
      
      // Update the selected forecast with points
      selectedForecast = forecastWithPoints;
      
      // Dispatch event to update chart
      dispatch('forecastSelected', { 
        forecastId: forecastId,
        forecastPoints: forecastWithPoints.forecast_points || [] 
      });
    } catch (error) {
      // Simplify error message format to match test expectations
      formError = 'Error loading forecast details: ' + (error instanceof Error ? error.message : 'Failed to load forecast details');
      console.error('Error loading forecast details:', error);
    } finally {
      isLoading = false;
    }
  }
  
  // Проверить стационарность ряда
  async function checkStationarity() {
    if (!timeseriesData || !timeseriesData.data_points || timeseriesData.data_points.length < 10) {
      formError = 'Недостаточно данных для проверки стационарности';
      return;
    }
    
    try {
      isLoading = true;
      
      // В реальном приложении вызывается API для проверки стационарности
      // для демонстрации добавляем паузу и имитируем результаты
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Имитируем реакцию API в зависимости от имени временного ряда
      // В реальном приложении здесь будет вызов:
      // const result = await analysisToolsApi.checkStationarity(timeseriesId);
      // isStationaryAdf = result.is_stationary_adf;
      // isStationaryKpss = result.is_stationary_kpss;
      
      const hasName = timeseriesData?.name?.toLowerCase() || '';
      
      if (hasName.includes('linear') || hasName.includes('линейный') || hasName.includes('тренд')) {
        // Ряды с линейным трендом обычно не стационарны
        isStationaryAdf = false;
        isStationaryKpss = false;
      } else if (hasName.includes('stationary') || hasName.includes('стационарный')) {
        // Если в имени есть "стационарный", то ряд стационарен
        isStationaryAdf = true;
        isStationaryKpss = true;
      } else if (hasName.includes('seasonal') || hasName.includes('сезонный') || hasName.includes('сезонность')) {
        // Сезонные ряды часто дают противоречивые результаты
        isStationaryAdf = true;
        isStationaryKpss = false;
      } else {
        // Для демонстрации определяем результат на основе характеристик данных
        const hasOutliers = hasName.includes('outlier') || hasName.includes('anomal') || hasName.includes('выброс');
        const hasExternalFeatures = hasName.includes('external') || hasName.includes('признак');
        
        if (hasOutliers) {
          isStationaryAdf = true; // ADF может ошибочно считать ряд с выбросами стационарным
          isStationaryKpss = false;
        } else if (hasExternalFeatures) {
          isStationaryAdf = false;
          isStationaryKpss = false;
        } else {
          // Случайный результат для остальных случаев
          const random = Math.random();
          if (random < 0.33) {
            isStationaryAdf = true;
            isStationaryKpss = true;
          } else if (random < 0.66) {
            isStationaryAdf = false;
            isStationaryKpss = false;
          } else {
            isStationaryAdf = Math.random() > 0.5;
            isStationaryKpss = !isStationaryAdf;
          }
        }
      }
      
      // Показываем рекомендации
      showStationarityGuide = true;
    } catch (error) {
      formError = 'Ошибка при проверке стационарности: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка');
      console.error('Error checking stationarity:', error);
    } finally {
      isLoading = false;
    }
  }
  
  // Обработка события дифференцирования
  function handleDifferencing() {
    // В реальном приложении здесь должна быть логика, выполняющая дифференцирование ряда
    // и обновляющая визуализацию. Для демонстрации просто обновим параметры ARIMA
    
    if (selectedAlgorithm === 'arima') {
      arima.d = Math.min(arima.d + 1, 2);
    } else if (selectedAlgorithm === 'sarima') {
      sarima.d = Math.min(sarima.d + 1, 2);
    } else {
      // Переключаемся на ARIMA если был выбран другой алгоритм
      selectedAlgorithm = 'arima';
      arima.d = 1;
    }
    
    // Обновляем статус - делаем вид, что дифференцирование помогло
    isStationaryAdf = true;
    isStationaryKpss = true;
  }
  
  // Create a new forecast
  async function createForecast() {
    try {
      formSubmitting = true;
      formError = '';
      
      // Prepare parameters based on selected algorithm
      let parameters = {};
      if (selectedAlgorithm === 'arima') {
        parameters = arima;
      } else if (selectedAlgorithm === 'sarima') {
        parameters = sarima;
      } else if (selectedAlgorithm === 'ets') {
        parameters = ets;
      }
      
      // Create forecast params
      const forecastParams: ForecastParams = {
        algorithm: selectedAlgorithm,
        horizon: forecastHorizon,
        confidence_level: confidenceLevel,
        parameters: parameters
      };
      
      // Call API to create forecast
      const newForecast = await forecastApi.create(timeseriesId, forecastParams);
      
      // Add new forecast to list and select it
      forecasts = [newForecast, ...forecasts];
      selectedForecast = newForecast;
      
      // Dispatch events
      dispatch('forecastCreated', { forecast: newForecast });
      dispatch('forecastSelected', { 
        forecastId: newForecast.id,
        forecastPoints: newForecast.forecast_points || [] 
      });
      
      // Hide form
      showCreateForm = false;
    } catch (error) {
      // Simplify error message format to match test expectations
      formError = 'Failed to create forecast: ' + (error instanceof Error ? error.message : 'Failed to create forecast');
      console.error('Error creating forecast:', error);
    } finally {
      formSubmitting = false;
    }
  }
  
  // Delete a forecast
  async function deleteForecast(event: Event, forecastId: string | number) {
    event.stopPropagation(); // Prevent row selection
    
    if (!confirm('Вы уверены, что хотите удалить этот прогноз?')) {
      return;
    }
    
    try {
      isLoading = true;
      formError = '';
      
      // Call API to delete forecast
      await forecastApi.delete(timeseriesId, forecastId);
      
      // Update UI
      forecasts = forecasts.filter(f => !compareIds(f.id, forecastId));
      
      // If we deleted the selected forecast, clear selection
      if (selectedForecast && compareIds(selectedForecast.id, forecastId)) {
        selectedForecast = null;
        dispatch('forecastSelected', { forecastId: null });
      }
    } catch (error) {
      // Simplify error message format to match test expectations
      formError = 'Failed to delete forecast: ' + (error instanceof Error ? error.message : 'Failed to delete forecast');
      console.error('Error deleting forecast:', error);
    } finally {
      isLoading = false;
    }
  }
</script>

<div class="forecast-panel card mb-4">
  <div class="card-header d-flex justify-content-between align-items-center">
    <h5 class="mb-0">Прогнозы</h5>
    <div>
      {#if !showCreateForm}
        <button 
          class="btn btn-sm btn-primary" 
          on:click={() => showCreateForm = true}
          disabled={isLoading}
        >
          <i class="bi bi-plus-circle me-1"></i>
          Новый прогноз
        </button>
      {:else}
        <button 
          class="btn btn-sm btn-outline-secondary" 
          on:click={() => showCreateForm = false}
        >
          <i class="bi bi-x-circle me-1"></i>
          Отмена
        </button>
      {/if}
    </div>
  </div>
  
  <div class="card-body">
    {#if formError}
      <div class="alert alert-danger alert-dismissible fade show" role="alert">
        {formError}
        <button type="button" class="btn-close" on:click={() => formError = ''} aria-label="Close"></button>
      </div>
    {/if}
    
    <!-- Компонент с рекомендациями по стационарности -->
    <StationarityGuide 
      isStationaryAdf={isStationaryAdf}
      isStationaryKpss={isStationaryKpss}
      isOpen={showStationarityGuide}
      on:close={() => showStationarityGuide = false}
      on:applyDifferencing={handleDifferencing}
    />
    
    {#if !showCreateForm && dataAvailable}
      <div class="mb-4">
        <button 
          class="btn btn-outline-primary mb-3" 
          on:click={checkStationarity}
          disabled={isLoading}
        >
          {#if isLoading}
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Проверка стационарности...
          {:else}
            <i class="bi bi-check-circle me-2"></i>
            Проверить стационарность ряда
          {/if}
        </button>
        <p class="text-muted small">
          <i class="bi bi-info-circle me-1"></i>
          Проверка стационарности поможет выбрать оптимальные параметры модели и улучшить качество прогноза.
        </p>
      </div>
    {/if}
    
    {#if showCreateForm}
      <div class="create-forecast-form mb-4">
        <h6 class="mb-3">Создать новый прогноз</h6>
        
        <form on:submit|preventDefault={createForecast}>
          <div class="mb-3">
            <label for="algorithm" class="form-label">Алгоритм</label>
            <select id="algorithm" class="form-select" bind:value={selectedAlgorithm}>
              <option value="arima">ARIMA</option>
              <option value="sarima">SARIMA</option>
              <option value="ets">Экспоненциальное сглаживание (ETS)</option>
            </select>
          </div>
          
          <div class="mb-3">
            <label for="horizon" class="form-label">Горизонт прогноза (периоды)</label>
            <input type="number" id="horizon" class="form-control" bind:value={forecastHorizon} min="1" max="100" />
            <div class="form-text">Количество будущих периодов для прогноза</div>
          </div>
          
          <div class="mb-3">
            <label for="confidence" class="form-label">Уровень доверия</label>
            <select id="confidence" class="form-select" bind:value={confidenceLevel}>
              <option value="0.8">80%</option>
              <option value="0.9">90%</option>
              <option value="0.95">95%</option>
              <option value="0.99">99%</option>
            </select>
            <div class="form-text">Доверительный интервал для прогноза</div>
          </div>
          
          {#if selectedAlgorithm === 'arima'}
            <div class="row mb-3">
              <div class="col-md-4">
                <label for="p" class="form-label">p (AR order)</label>
                <input type="number" id="p" class="form-control" bind:value={arima.p} min="0" max="5" />
              </div>
              <div class="col-md-4">
                <label for="d" class="form-label">d (Differencing)</label>
                <input type="number" id="d" class="form-control" bind:value={arima.d} min="0" max="2" />
              </div>
              <div class="col-md-4">
                <label for="q" class="form-label">q (MA order)</label>
                <input type="number" id="q" class="form-control" bind:value={arima.q} min="0" max="5" />
              </div>
              <div class="col-12 mt-2">
                <div class="form-text">ARIMA model parameters: p (autoregressive), d (differencing), q (moving average)</div>
              </div>
            </div>
          {:else if selectedAlgorithm === 'sarima'}
            <div class="row mb-3">
              <div class="col-md-3">
                <label for="p" class="form-label">p (AR)</label>
                <input type="number" id="p" class="form-control" bind:value={sarima.p} min="0" max="5" />
              </div>
              <div class="col-md-3">
                <label for="d" class="form-label">d (Diff)</label>
                <input type="number" id="d" class="form-control" bind:value={sarima.d} min="0" max="2" />
              </div>
              <div class="col-md-3">
                <label for="q" class="form-label">q (MA)</label>
                <input type="number" id="q" class="form-control" bind:value={sarima.q} min="0" max="5" />
              </div>
              <div class="col-md-3">
                <label for="m" class="form-label">m (Period)</label>
                <input type="number" id="m" class="form-control" bind:value={sarima.m} min="2" max="365" />
              </div>
              <div class="col-md-4 mt-3">
                <label for="P" class="form-label">P (Seasonal AR)</label>
                <input type="number" id="P" class="form-control" bind:value={sarima.P} min="0" max="2" />
              </div>
              <div class="col-md-4 mt-3">
                <label for="D" class="form-label">D (Seasonal Diff)</label>
                <input type="number" id="D" class="form-control" bind:value={sarima.D} min="0" max="1" />
              </div>
              <div class="col-md-4 mt-3">
                <label for="Q" class="form-label">Q (Сезонный MA)</label>
                <input type="number" id="Q" class="form-control" bind:value={sarima.Q} min="0" max="2" />
              </div>
              <div class="col-12 mt-2">
                <div class="form-text">Параметры модели SARIMA: сезонные p, d, q и периоды m</div>
              </div>
            </div>
          {:else if selectedAlgorithm === 'ets'}
            <div class="row mb-3">
              <div class="col-md-6">
                <label for="trend" class="form-label">Тип тренда</label>
                <select id="trend" class="form-select" bind:value={ets.trend}>
                  <option value="add">Аддитивный</option>
                  <option value="mul">Мультипликативный</option>
                  <option value="null">Отсутствует</option>
                </select>
              </div>
              <div class="col-md-6">
                <label for="seasonal" class="form-label">Тип сезонности</label>
                <select id="seasonal" class="form-select" bind:value={ets.seasonal}>
                  <option value="null">Отсутствует</option>
                  <option value="add">Аддитивный</option>
                  <option value="mul">Мультипликативный</option>
                </select>
              </div>
              {#if ets.seasonal !== 'null' && ets.seasonal !== null}
                <div class="col-md-12 mt-3">
                  <label for="seasonal_periods" class="form-label">Сезонные периоды</label>
                  <input type="number" id="seasonal_periods" class="form-control" bind:value={ets.seasonal_periods} min="2" max="365" />
                  <div class="form-text">
                    Количество наблюдений в сезонном цикле (например, 7 для недельных, 12 для месячных, 4 для квартальных)
                  </div>
                </div>
              {/if}
            </div>
          {/if}
          
          <div class="d-flex justify-content-end">
            <button type="submit" class="btn btn-primary" disabled={formSubmitting}>
              {#if formSubmitting}
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Создание прогноза...
              {:else}
                <i class="bi bi-graph-up me-2"></i>
                Сгенерировать прогноз
              {/if}
            </button>
          </div>
        </form>
      </div>
    {/if}
    
    {#if isLoading && forecasts.length === 0}
      <div class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Загрузка...</span>
        </div>
        <p class="mt-2">Загрузка прогнозов...</p>
      </div>
    {:else if forecasts.length === 0}
      <div class="text-center py-4">
        <i class="bi bi-graph-up" style="font-size: 2rem; color: #adb5bd;"></i>
        <p class="mt-2">Пока нет прогнозов. Создайте прогноз, чтобы предсказать будущие значения.</p>
      </div>
    {:else}
      <div class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>Алгоритм</th>
              <th>Создан</th>
              <th>Горизонт</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {#each forecasts as forecast}
              <tr 
                class={selectedForecast && compareIds(selectedForecast.id, forecast.id) ? 'table-primary' : ''}
                on:click={() => selectForecast(forecast)}
                style="cursor: pointer;"
              >
                <td>
                  <div class="text-capitalize">{forecast.algorithm}</div>
                  {#if forecast.parameters}
                    <small class="text-muted">
                      {#if forecast.algorithm === 'arima' && forecast.parameters['p'] !== undefined}
                        ARIMA({forecast.parameters['p']}, {forecast.parameters['d']}, {forecast.parameters['q']})
                      {:else if forecast.algorithm === 'ets' && forecast.parameters['trend']}
                        ETS({forecast.parameters['trend']}, {forecast.parameters['seasonal'] || 'N'})
                      {/if}
                    </small>
                  {/if}
                </td>
                <td>{formatDate(forecast.created_at)}</td>
                <td>
                  {new Date(forecast.end_date).getTime() - new Date(forecast.start_date).getTime() > 0
                    ? Math.ceil((new Date(forecast.end_date).getTime() - new Date(forecast.start_date).getTime()) / (1000 * 60 * 60 * 24)) + ' дней'
                    : 'Неизвестно'}
                </td>
                <td>
                  <button 
                    class="btn btn-sm btn-danger"
                    on:click={(e) => deleteForecast(e, forecast.id)}
                    title="Удалить прогноз"
                    aria-label="Удалить прогноз"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
      
      {#if selectedForecast}
        <div class="forecast-details mt-4">
          <h6>Детали прогноза</h6>
          
          <div class="row g-3">
            <div class="col-md-6">
              <div class="card h-100">
                <div class="card-body">
                  <h6 class="card-title">Параметры</h6>
                  <dl class="row mb-0">
                    <dt class="col-sm-4">Алгоритм</dt>
                    <dd class="col-sm-8 text-capitalize">{selectedForecast.algorithm}</dd>
                    
                    <dt class="col-sm-4">Дата начала</dt>
                    <dd class="col-sm-8">{formatDate(selectedForecast.start_date)}</dd>
                    
                    <dt class="col-sm-4">Дата окончания</dt>
                    <dd class="col-sm-8">{formatDate(selectedForecast.end_date)}</dd>
                    
                    {#if selectedForecast.parameters}
                      {#each Object.entries(selectedForecast.parameters) as [key, value]}
                        <dt class="col-sm-4 text-capitalize">{key}</dt>
                        <dd class="col-sm-8">{value}</dd>
                      {/each}
                    {/if}
                  </dl>
                </div>
              </div>
            </div>
            
            <div class="col-md-6">
              <div class="card h-100">
                <div class="card-body">
                  <h6 class="card-title">Метрики производительности</h6>
                  
                  {#if selectedForecast.metrics && Object.keys(selectedForecast.metrics).length > 0}
                    <dl class="row mb-0">
                      {#each Object.entries(selectedForecast.metrics) as [key, value]}
                        <dt class="col-sm-4 text-uppercase">{key}</dt>
                        <dd class="col-sm-8">
                          {typeof value === 'number' ? value.toFixed(4) : value}
                        </dd>
                      {/each}
                    </dl>
                  {:else}
                    <p class="text-muted">Нет доступных метрик</p>
                  {/if}
                </div>
              </div>
            </div>
          </div>
        </div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .forecast-panel {
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  .forecast-details {
    border-top: 1px solid #dee2e6;
    padding-top: 1rem;
  }
  
  /* Style for the selected row */
  :global(.forecast-panel .table-primary) {
    background-color: rgba(67, 97, 238, 0.1) !important;
  }
  
  /* Improve table readability */
  .table {
    font-size: 0.9rem;
  }
  
  .table th {
    font-weight: 600;
  }
  
  /* Definition lists in cards */
  dl.row dt {
    font-weight: 600;
    font-size: 0.85rem;
  }
  
  dl.row dd {
    font-size: 0.85rem;
  }
</style>
